import { useTranslation } from "react-i18next";
import { TbCreditCard } from "react-icons/tb";
import { DetailCard, DetailRow } from "src/components/Details/Details";
import RemainingAmount from "src/components/RemainingAmount/RemainingAmount";
import { Invoice } from "src/types";

interface InvoiceAmountsCardProps {
    invoice: Invoice | null;
    paymentDelta: number | "";
    setPaymentDelta: (v: number | "") => void;
    vatRate?: number;
    paymentEntryBlocked?: boolean;
    fineRemoved?: boolean;
    onRemoveFine?: () => void;
    isPaid?: boolean;
    isWrittenOff?: boolean;
}

export function InvoiceAmountsCard({
    invoice,
    paymentDelta,
    setPaymentDelta,
    vatRate = 0,
    paymentEntryBlocked = false,
    fineRemoved = false,
    onRemoveFine,
    isPaid = false,
    isWrittenOff = false,
}: InvoiceAmountsCardProps) {
    const { t } = useTranslation();
    const fee          = invoice?.amount ?? 0;
    const materialCosts = invoice?.materialCosts ?? 0;
    const net         = +(fee + materialCosts).toFixed(2);
    // Prefer the backend's precomputed per-line aggregate (correct even when exams carry
    // different VAT rates); only invoices that predate that field (never recalculated
    // since) fall back to the old flat "net * vatRate / 100" formula.
    const vatAmount   = invoice?.vatAmount ?? (vatRate > 0 ? +(net * vatRate / 100).toFixed(2) : 0);
    const vatBreakdown = invoice?.vatBreakdown ?? (vatRate > 0 ? [{ rate: vatRate, base: net, vat: vatAmount }] : []);
    // "The whole invoice has VAT" — every line shares the one nonzero rate, nothing exempt
    // mixed in. Only then is a Net/VAT row meaningful; showing "VAT (7%)" against the FULL
    // net would be misleading the moment even one line is exempt or taxed differently — that
    // case is instead explained per line (see ExamTable.tsx) and just totals up here.
    const exemptBase  = +(net - vatBreakdown.reduce((s, r) => s + r.base, 0)).toFixed(2);
    const vatUniform  = vatBreakdown.length === 1 && Math.abs(exemptBase) < 0.005;
    const gross       = +(net + vatAmount).toFixed(2);
    const totalPaid   = invoice?.paidAmount ?? 0;
    const newPayment  = Number(paymentDelta) || 0;
    const fine        = invoice?.dunningFineAmount ?? 0;
    const totalBase   = gross + fine;
    const combinedPaid = totalPaid + newPayment;
    const remaining   = Math.max(0, totalBase - combinedPaid);
    // Every write path (bank auto-reconcile, manual match, manual payment entry) currently
    // accepts any amount with no upper-bound check — this is purely a display fix making an
    // overpayment visible instead of silently clamping to €0.00; no automatic refund/correction.
    const overpaid    = Math.max(0, +(combinedPaid - totalBase).toFixed(2));
    // If payments already received cover the fee+fine total, the fine has effectively already
    // been paid — waiving it now can't refund anything, it only turns that same amount into a
    // confusing phantom "overpaid" balance. Mirrors the backend guard in invoiceController.ts
    // (patchInvoice), which rejects the write outright; this just keeps the button from
    // inviting an action the server will refuse. Uses totalPaid (not combinedPaid) since a
    // pending, unsaved paymentDelta shouldn't unlock/lock this.
    const fineAlreadyPaid = fine > 0 && totalPaid >= totalBase - 0.01;

    return (
        <DetailCard title={t("invoicePage.amounts")}>
            {/* Remaining balance hero — most important info first */}
            <div className={`amounts-remaining-hero${overpaid > 0 ? " amounts-remaining-hero--overpaid" : ""}`}>
                <div className="amounts-remaining-hero__label">{t("invoicePage.outstandingBalance")}</div>
                <div className="amounts-remaining-hero__amount">€ {remaining.toFixed(2)}</div>
                <div className="amounts-remaining-hero__sub">
                    € {combinedPaid.toFixed(2)} {t("invoicePage.paid")} · € {remaining.toFixed(2)} {t("invoicePage.remaining")}
                </div>
                {overpaid > 0 && (
                    <div className="amounts-remaining-hero__overpaid">
                        ⚠ {t("invoicePage.overpaidBy", { amount: overpaid.toFixed(2) })}
                    </div>
                )}
            </div>

            {vatUniform ? (
                <>
                    <DetailRow label={t("invoicePage.net")} value={`€ ${net.toFixed(2)}`} />
                    <DetailRow label={t("invoicePage.vat", { rate: vatBreakdown[0].rate })} value={`€ ${vatAmount.toFixed(2)}`} />
                    <DetailRow label={t("invoicePage.total")} value={<strong>€ {gross.toFixed(2)}</strong>} />
                </>
            ) : (
                <DetailRow label={t("invoicePage.total")} value={`€ ${gross.toFixed(2)}`} />
            )}
            {fine > 0 && (
                <DetailRow
                    label={t("invoicePage.dunningFine")}
                    value={
                        <span className={`amounts-fine-pill${fineRemoved ? " amounts-fine-pill--removed" : ""}`}>
                            + € {fine.toFixed(2)}
                            <span
                                className="fine-info-icon"
                                data-tooltip={t("invoicePage.dunningFineTooltip", {
                                    amount: fine.toFixed(2),
                                    date: invoice?.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : "—",
                                })}
                            >
                                ℹ
                            </span>
                            {onRemoveFine && !fineRemoved && !isWrittenOff && (
                                <button
                                    className="fine-remove-btn"
                                    onClick={fineAlreadyPaid ? undefined : onRemoveFine}
                                    disabled={fineAlreadyPaid}
                                    title={fineAlreadyPaid ? t("invoicePage.fineAlreadyPaidHint") : t("invoicePage.removeFine")}
                                >
                                    ×
                                </button>
                            )}
                        </span>
                    }
                />
            )}
            {fine > 0 && (
                <DetailRow label={t("invoicePage.totalWithFine")} value={<strong>€ {(gross + fine).toFixed(2)}</strong>} />
            )}
            <DetailRow label={t("invoicePage.paidTotal")} value={<span className="inv-amounts-paid">€ {totalPaid.toFixed(2)}</span>} />
            {/* Card payments from the patient portal (SumUp) — part of "paid" above, listed so the
                practice can find each one in its SumUp account by transaction code. */}
            {(invoice?.cardPayments ?? []).map((p, i) => (
                <DetailRow
                    key={`${p.paidAt}-${i}`}
                    label={<span className="inv-amounts-card"><TbCreditCard /> {t("invoicePage.cardPayment")}</span>}
                    value={
                        <span className="inv-amounts-card__value">
                            € {p.amount.toFixed(2)}
                            <span className="inv-amounts-card__meta">
                                {new Date(p.paidAt).toLocaleDateString()}
                                {p.transactionCode && ` · ${p.transactionCode}`}
                            </span>
                        </span>
                    }
                />
            ))}
            <DetailRow
                label={t("invoicePage.newPayment")}
                value={
                    <div className="invoice-payment-entry">
                        <input
                            className="invoice-input"
                            type="number"
                            aria-label={t("invoicePage.newPayment")}
                            min={0}
                            step={0.01}
                            value={paymentDelta}
                            placeholder="0.00"
                            disabled={paymentEntryBlocked || isPaid || isWrittenOff}
                            title={paymentEntryBlocked ? t("invoicePage.paymentBlockedHint") : undefined}
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "+" || e.key === "e" || e.key === "E") e.preventDefault();
                            }}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === "") { setPaymentDelta(""); return; }
                                const n = Number(raw);
                                setPaymentDelta(Number.isFinite(n) ? Math.max(0, n) : "");
                            }}
                        />
                        {paymentEntryBlocked && (
                            <div className="invoice-payment-entry__hint">{t("invoicePage.paymentBlockedHint")}</div>
                        )}
                    </div>
                }
            />
            <DetailRow label={t("invoicePage.materialCosts")} value={`€ ${invoice?.materialCosts?.toFixed(2) ?? "0.00"}`} />
        </DetailCard>
    );
}
